import * as React from "react";

import {
    setPhysicsModel,
    physics2localization,
    launchHeightInches2HeadHeight,
    getPhysicsModel,
} from "@volley/physics/compat/convert";
import { PhysicsModelName } from "@volley/physics/dist/models";
import { UpdatePosition } from "@volley/shared/http/trainer-control";
import type {
    VisionLocalizationStatus,
    Coordinate,
    FullPosition,
    CameraPositionTolerance,
    CameraPosition,
} from "@volley/shared/vision-models";

import logger from "../../log";
import { pairedFetchApi, logFetchError } from "../../util/fetchApi";
import { PositionLike } from "../../util/position-types";
import { degToRad } from "../../util/positionUtil";
import { isDeprecatedLocalizationStatus } from "../Trainer/util/status";
import { Sport, useSelectedSport } from "../common/context/sport";

import useRanges from "./ranges";
import { useStatus } from "./status";
import { useTrainerFeatures } from "./useTrainerFeatures";

export interface PlannedPosition {
    x: number;
    y: number;
    yaw: number;
    heightIn: number;
}

export type LocalizationResult = "fail" | "good" | "improve";

export type PositionWithHeight = PositionLike & { heightIn: number };

export type LocalizationStatus =
    | "idle"
    | "requested"
    | "pending"
    | "cancelling"
    | "localizing"
    | "unavailable"
    | "error";
export type PositionProximity =
    | "Good"
    | "Improve"
    | "Unsure"
    | "Unavailable"
    | "Not Ready";
interface PositionStateInternal {
    requestedAt: number | undefined;
    error: string | undefined;
    position: FullPosition | undefined;
    improve: boolean;
    isVisionStarting: boolean;
    isVisionFaulted: boolean;
    localizationStatus: LocalizationStatus;
    delta: Coordinate | undefined;
    method: LocalizationType;
    ualResult: CameraPosition | null;
}

export interface PositionState extends PositionStateInternal {
    isLocalizing: boolean;
    isCanceled: boolean;
    updatePosition: (
        hintPosition?: PositionWithHeight,
        oneShot?: boolean,
        cameraPositionTolerance?: CameraPositionTolerance,
    ) => void;
    cancel: () => void;
    setUalResult: (result: CameraPosition | null) => void;
    clearUalResult: () => void;
    isCloseEnough: (
        plannedPosition: PositionWithHeight,
        sport: Sport,
        physicsModelName: PhysicsModelName,
        compareHeight: boolean,
    ) => LocalizationResult;
    isPlannedPositionCloseEnough: (
        plannedPosition: PositionWithHeight,
        sport: Sport,
        physicsModelName: PhysicsModelName,
        compareHeight: boolean,
    ) => LocalizationResult;
}

export type LocalizationType = "one-shot" | "smart";

/**
 * Can vision localize this sport?
 *
 * Lacrosse is deliberately absent. It was briefly included on the reasoning
 * that vision knows the sport and could match it against the tennis court
 * model, but there is no lacrosse model yet, so what came back was a position
 * derived from court lines that are not painted on the field.
 */
export function localizationSupported(sport: Sport): boolean {
    return (
        sport === "TENNIS" ||
        sport === "PLATFORM_TENNIS" ||
        sport === "PICKLEBALL"
    );
}

/**
 * Can this sport use user-assisted localization (UAL)?
 *
 * Not the same question as localizationSupported. UAL aligns the camera from a
 * snapshot the user confirms, and padel has always used it even though
 * fetchPosition cannot localize padel. Gating UAL on localizationSupported
 * shut every padel trainer out of Zone Triggered and Serve & Volley behind
 * "Trainer calibration required" (#8401).
 *
 * Lacrosse is deliberately absent: vision has no lacrosse model, so there is
 * nothing on a field for a snapshot to be solved against. An allowlist rather
 * than a lacrosse exclusion so that a sport added later does not get UAL
 * before vision can solve its court.
 */
export function ualSupported(sport: Sport): boolean {
    return localizationSupported(sport) || sport === "PADEL";
}

const defaultPositionStateInternal: PositionStateInternal = {
    requestedAt: undefined,
    error: undefined,
    position: undefined,
    improve: false,
    isVisionStarting: true,
    isVisionFaulted: false,
    localizationStatus: "idle",
    delta: undefined,
    method: "smart",
    ualResult: null,
};

const defaultLocalizationState: PositionState = {
    ...defaultPositionStateInternal,
    isLocalizing: false,
    isCanceled: false,
    updatePosition: () => {},
    cancel: () => {},
    setUalResult: () => {},
    clearUalResult: () => {},
    isCloseEnough: () => "fail",
    isPlannedPositionCloseEnough: () => "fail",
};

export const LocalizationContext = React.createContext<PositionState>(
    defaultLocalizationState,
);

const DISTANCE_DELTA_ALLOWED_BY_SPORT = {
    PLATFORM_TENNIS: 500,
    PADEL: 500,
    TENNIS: 500,
    PICKLEBALL: 500,
    LACROSSE: 500,
};

const HEIGHT_DELTA_ALLOWED_BY_SPORT = {
    PLATFORM_TENNIS: 100,
    PADEL: 100,
    TENNIS: 150,
    PICKLEBALL: 100,
    // Lacrosse localizes against the tennis court model, so it takes tennis's
    // tolerances.
    LACROSSE: 150,
};

const YAW_DELTA_ALLOWED_BY_SPORT = {
    PLATFORM_TENNIS: 15,
    PADEL: 4,
    PICKLEBALL: 4,
    TENNIS: 2,
    LACROSSE: 2,
};

function absoluteDiff(a: number, b: number) {
    if ((a > 0 && b > 0) || (a < 0 && b < 0)) {
        return (
            Math.max(Math.abs(a), Math.abs(b)) -
            Math.min(Math.abs(a), Math.abs(b))
        );
    }

    return Math.abs(a) + Math.abs(b);
}

function cameraPositionToFullPosition(
    cameraPosition: CameraPosition | null,
): FullPosition | undefined {
    if (!cameraPosition) return undefined;
    return {
        x: cameraPosition.x,
        y: cameraPosition.y,
        z: cameraPosition.z,
        error: null,
        debug: null,
        intersections: {
            bottomLeft: {
                x: 0,
                y: 0,
            },
            bottomRight: {
                x: 0,
                y: 0,
            },
            topLeft: {
                x: 0,
                y: 0,
            },
            topRight: {
                x: 0,
                y: 0,
            },
        },
        pitch: cameraPosition.pitch,
        roll: cameraPosition.roll,
        yaw: cameraPosition.yaw,
    };
}

function cameraPositionDeltaToDelta(
    cameraPositionDelta: CameraPosition | null,
): Coordinate | undefined {
    if (!cameraPositionDelta) return undefined;
    return {
        x: cameraPositionDelta.x,
        y: cameraPositionDelta.y,
        yaw: cameraPositionDelta.yaw,
    };
}

type PositionStateChange =
    | { type: "set-error"; value: string }
    | { type: "set-faulted"; value: boolean }
    | { type: "request-cancel" }
    | { type: "cancel-confirmed" }
    | { type: "request-localization"; value: LocalizationType }
    | { type: "vision-status"; value: boolean }
    | { type: "localization-unavailable" }
    | { type: "localization-confirmed" }
    | { type: "localization-confirm-fail"; value: string }
    | { type: "localization-in-progress" }
    | { type: "completed"; value: VisionLocalizationStatus }
    | { type: "set-ual-result"; value: CameraPosition | null }
    | { type: "clear-ual-result" };

function positionReducer(
    state: PositionStateInternal,
    action: PositionStateChange,
): PositionStateInternal {
    switch (action.type) {
        case "vision-status":
            return {
                ...state,
                isVisionStarting: action.value,
            };
        case "set-error":
            return {
                ...state,
                error: action.value,
            };
        case "set-faulted":
            return {
                ...state,
                isVisionFaulted: action.value,
                isVisionStarting: !action.value,
            };
        case "request-cancel":
            return {
                ...state,
                localizationStatus: "cancelling",
                error: undefined,
                improve: false,
                position: undefined,
                requestedAt: performance.now(),
            };
        case "cancel-confirmed":
            return {
                ...defaultPositionStateInternal,
                isVisionStarting: state.isVisionStarting,
            };
        case "request-localization":
            return {
                ...defaultPositionStateInternal,
                method: action.value,
                isVisionStarting: state.isVisionStarting,
                localizationStatus: "pending",
                requestedAt: performance.now(),
            };
        case "localization-unavailable":
            return {
                ...defaultPositionStateInternal,
                isVisionStarting: state.isVisionStarting,
                localizationStatus: "unavailable",
            };
        case "localization-confirmed":
            return {
                ...state,
                localizationStatus: "localizing",
                method: state.method,
                requestedAt: undefined,
            };
        case "localization-in-progress":
            return {
                ...defaultLocalizationState,
                method: state.method,
                isVisionStarting: state.isVisionStarting,
                localizationStatus: "localizing",
            };
        case "localization-confirm-fail":
            return {
                ...state,
                localizationStatus: "error",
                error: action.value,
                requestedAt: undefined,
            };
        case "completed": {
            if (isDeprecatedLocalizationStatus(action.value)) {
                return {
                    ...defaultPositionStateInternal,
                    isVisionStarting: state.isVisionStarting,
                    method: state.method,
                    position: action.value.position ?? undefined,
                    error: action.value.position?.error ?? undefined,
                    improve: action.value.improve,
                    delta: action.value.delta,
                    ualResult: state.ualResult,
                };
            } else {
                return {
                    ...defaultPositionStateInternal,
                    isVisionStarting: state.isVisionStarting,
                    method: state.method,
                    position: cameraPositionToFullPosition(
                        action.value.cameraPosition,
                    ),
                    error: action.value.cameraPosition ? undefined : "Failed",
                    improve: action.value.improve,
                    delta: cameraPositionDeltaToDelta(
                        action.value.cameraPositionDelta,
                    ),
                    ualResult: state.ualResult,
                };
            }
        }
        case "set-ual-result":
            return {
                ...state,
                ualResult: action.value,
            };
        case "clear-ual-result":
            return {
                ...state,
                ualResult: null,
            };
        default:
            return state;
    }
}

const DEFAULT_TOLERANCE = {
    maxGroundDistanceDeltaMeters: 0.5,
    maxHeightDeltaMeters: 0.15,
    maxRollDeltaDegrees: 1.7,
    maxPitchDeltaDegrees: 1.5,
    maxYawDeltaDegrees: 5.0,
};

export const LocalizationProvider = React.memo(
    ({ children }: React.PropsWithChildren) => {
        const { status } = useStatus();
        const { selected: sport } = useSelectedSport();
        const trainerFeatures = useTrainerFeatures();
        const {
            lift: { max: maxTrainerLiftHeight },
        } = useRanges();
        const [stateInternal, dispatch] = React.useReducer(
            positionReducer,
            defaultPositionStateInternal,
        );

        React.useEffect(() => {
            logger.info(
                `[usePosition] Internal State Changed: ${JSON.stringify(stateInternal)}`,
            );
        }, [stateInternal]);

        React.useEffect(() => {
            if (!status?.localization) {
                return;
            }
            const updatedVisionStarting =
                status?.vision?.visionSystemStarting ?? false;
            if (stateInternal.isVisionStarting !== updatedVisionStarting) {
                if (status?.vision?.serviceState !== "Fault") {
                    logger.info(
                        `[usePosition] - Setting vision status to '${updatedVisionStarting ? "Starting" : "Up"}'`,
                    );
                    dispatch({
                        type: "vision-status",
                        value: updatedVisionStarting,
                    });
                }
            }

            if (
                status?.vision?.serviceState === "Fault" &&
                !stateInternal.isVisionFaulted
            ) {
                logger.info("[usePosition] - Vision reporting a fault.");
                dispatch({ type: "set-faulted", value: true });
                return;
            }

            if (stateInternal.isVisionFaulted) {
                if (
                    status?.vision?.serviceState === "Running" &&
                    status?.vision?.visionSystemStarting !== true
                ) {
                    logger.info("[usePosition] - Vision no longer faulted");
                    dispatch({ type: "set-faulted", value: false });
                    return;
                }
            }

            const localizing = status?.localization?.isActive ?? false;
            // We asked for a localization job and coach has now confirmed it is running
            if (localizing && stateInternal.localizationStatus === "pending") {
                logger.info(
                    "[usePosition] - Localization was Pending - Confirmed by Coach Status",
                );
                dispatch({ type: "localization-confirmed" });
                return;
            }

            // This can happen if localization is in progress and the user refreshes/navigates
            if (localizing && stateInternal.localizationStatus === "idle") {
                logger.info(
                    "[usePosition] - Localization  was idle, but is active from state - resetting internal state",
                );
                dispatch({ type: "localization-in-progress" });
                return;
            }

            // We were tracking a localization task, but coach's status indicates it is no longer active
            if (
                !localizing &&
                stateInternal.localizationStatus === "localizing"
            ) {
                logger.info(
                    "[usePosition] - Localization was active, status indicates it is no longer active",
                );
                dispatch({ type: "completed", value: status?.localization });
                return;
            }

            // We asked to cancel the running localization job and coach is validating our request
            if (
                !localizing &&
                stateInternal.localizationStatus === "cancelling"
            ) {
                logger.info(
                    "[usePosition] - Cancellation confirmed, status indicates it is no longer active",
                );
                dispatch({ type: "cancel-confirmed" });
                return;
            }

            // Clear started startedLocalizing if we have gotten a sufficiently up-to-date status
            if (
                stateInternal.requestedAt &&
                performance.now() > stateInternal.requestedAt + 10_000
            ) {
                dispatch({
                    type: "localization-confirm-fail",
                    value: "Status not confirmed after 5 seconds, aborting",
                });
            }
        }, [status?.vision, status?.localization, stateInternal]);

        const fetchPosition = React.useCallback(
            async (
                hintPosition?: PositionWithHeight,
                oneShot?: boolean,
                cameraPositionTolerance?: CameraPositionTolerance,
            ) => {
                if (stateInternal.isVisionStarting) {
                    logger.info(
                        "[usePosition] - returning early from 'fetchPosition' because vision is not yet ready",
                    );
                    dispatch({ type: "set-error", value: "NOT_READY" });
                    return;
                }

                if (stateInternal.isVisionFaulted) {
                    logger.info(
                        "[usePosition] - returning early from 'fetchPosition' because vision is not yet ready",
                    );
                    dispatch({ type: "set-error", value: "FAULTED" });
                    return;
                }

                if (stateInternal.localizationStatus !== "idle") {
                    logger.info(
                        `[usePosition] - returning early from 'fetchPosition': ${stateInternal.localizationStatus}`,
                    );
                    return;
                }

                /*
                    Lacrosse never localizes. This was written the other way -
                    lacrosse was included on the reasoning that vision knows the
                    sport and could match it against the tennis court model -
                    but vision has no lacrosse support yet, so what came back
                    was a position derived from court lines that are not on the
                    field.

                    Gated HERE rather than at the screens because this is the
                    one call every one of them makes: the accordions, the module
                    start flow, the AOI app's re-localize, the lift dialog.
                    Missing one would mean a trainer positioned from a reading
                    that means nothing.

                    Returned quietly instead of as UNSUPPORTED_SPORT: that error
                    surfaces to the coach, and nothing has gone wrong here - the
                    drill simply does not use localization. Screens already cope,
                    because they only consume a localized position for platform
                    tennis anyway.
                */
                if (sport === "LACROSSE") {
                    logger.info(
                        `[usePosition] - returning early from 'fetchPosition': vision has no lacrosse support yet`,
                    );
                    return;
                }

                if (!localizationSupported(sport)) {
                    logger.info(
                        `[usePosition] - returning early from 'fetchPosition': sport is not tennis or platform tennis or pickleball`,
                    );
                    dispatch({ type: "set-error", value: "UNSUPPORTED_SPORT" });
                    return;
                }

                const method = oneShot ? "one-shot" : "smart";
                logger.info(
                    `[usePosition] - Requesting localization with method: ${method}`,
                );
                dispatch({
                    type: "request-localization",
                    value: method,
                });

                try {
                    const payload: UpdatePosition = { hint: hintPosition };

                    // only pass oneshot if the trainer supports it, it was added with Serve and Volley
                    // this should allow backwards compatibility with older trainers
                    if (trainerFeatures.includes("serveAndVolley")) {
                        payload.oneshot = oneShot;
                    }

                    if (trainerFeatures.includes("localizationPipeline")) {
                        payload.sport = sport;
                    }

                    if (
                        cameraPositionTolerance &&
                        trainerFeatures.includes("failsafeLocalization")
                    ) {
                        payload.tolerance = cameraPositionTolerance;
                    } else if (
                        trainerFeatures.includes("failsafeLocalization")
                    ) {
                        payload.tolerance = DEFAULT_TOLERANCE;
                    }

                    await pairedFetchApi(
                        status?.clientId,
                        "/api/position",
                        "POST",
                        payload,
                    );
                } catch (e) {
                    logFetchError(
                        e,
                        "[usePosition] - Request to start localizing failed",
                    );
                    dispatch({
                        type: "localization-confirm-fail",
                        value: (e as Error).message,
                    });
                }
            },
            [sport, status?.clientId, stateInternal, trainerFeatures],
        );

        // SoftStop
        const cancel = React.useCallback(() => {
            logger.info(
                "[usePosition] - user requested localization cancellation",
            );
            dispatch({ type: "request-cancel" });
            pairedFetchApi(status?.clientId, "/api/cancel-position", "POST")
                .catch((e) => logFetchError(e))
                .finally(() => {
                    pairedFetchApi(
                        status?.clientId,
                        "/api/motors-stop",
                        "POST",
                    ).catch((stopError) => {
                        logFetchError(stopError);
                    });
                });
        }, [status?.clientId]);

        const isCloseEnough = React.useCallback(
            (
                plannedPosition: PlannedPosition,
                sport: Sport,
                physicsModelName: PhysicsModelName,
                compareHeight: boolean,
            ) => {
                if (
                    isDeprecatedLocalizationStatus(status?.localization) ||
                    stateInternal.ualResult
                ) {
                    if (stateInternal.ualResult) {
                        logger.info(
                            "[usePosition] closeEnough - using cached UAL position result",
                        );
                    } else {
                        logger.info(
                            "[usePosition] closeEnough - using deprecated localization status",
                        );
                    }
                    const position = stateInternal.ualResult
                        ? stateInternal.ualResult
                        : stateInternal.position;
                    if (!position) {
                        // logger.warn(
                        //     "[usePosition] closeEnough - no position available",
                        // );
                        return "fail";
                    }
                    const distanceDeltaAllowed =
                        DISTANCE_DELTA_ALLOWED_BY_SPORT[sport];
                    const heightDeltaAllowed =
                        HEIGHT_DELTA_ALLOWED_BY_SPORT[sport];
                    const yawDeltaAllowed = YAW_DELTA_ALLOWED_BY_SPORT[sport];

                    let comparePosition = { ...plannedPosition };
                    if (sport !== "PLATFORM_TENNIS") {
                        logger.info(
                            `Converting Planned Position: ${JSON.stringify(comparePosition)} for comparison`,
                        );
                        setPhysicsModel(physicsModelName, sport);
                        const asLocalized = physics2localization({
                            x: comparePosition.x,
                            y: comparePosition.y,
                            z: launchHeightInches2HeadHeight(
                                comparePosition.heightIn,
                            ),
                        });
                        comparePosition = {
                            x: asLocalized.x,
                            y: asLocalized.y,
                            yaw: degToRad(comparePosition.yaw),
                            heightIn: comparePosition.heightIn,
                        };
                        logger.info(
                            `Updated Compare Position: ${JSON.stringify(comparePosition)}`,
                        );
                    }

                    logger.info(
                        `Localized Position: ${JSON.stringify(position)}`,
                    );

                    const distance = Math.sqrt(
                        (position.x - comparePosition.x) ** 2 +
                            (position.y - comparePosition.y) ** 2,
                    );
                    const distanceGood = distance <= distanceDeltaAllowed;

                    logger.info(
                        `[usePosition]: Allowed Distance: ${distanceDeltaAllowed} - calculated distance: ${distance}, distance good? ${distanceGood ? "yes" : "no"}`,
                    );

                    let heightDiff = 0;
                    let heightGood = true;
                    const physicsModel = getPhysicsModel();
                    const launchPointToCameraDistanceMeters =
                        physicsModel.trainerGeometry.cameraOffsetFromHead.z -
                        physicsModel.trainerGeometry.launchOriginOffset.z;
                    if (compareHeight) {
                        logger.info(
                            "Comparing height as part of localization validation",
                        );
                        const launchPointToCameraDistanceMeters =
                            physicsModel.trainerGeometry.cameraOffsetFromHead
                                .z -
                            physicsModel.trainerGeometry.launchOriginOffset.z;
                        // compute planned camera height in mm from launch position height in inches
                        const plannedHeightAdjusted =
                            comparePosition.heightIn * 25.4 +
                            launchPointToCameraDistanceMeters * 1000.0;
                        heightDiff = Math.abs(
                            position.z - plannedHeightAdjusted,
                        );
                        heightGood = heightDiff <= heightDeltaAllowed;
                    } else {
                        // check to make sure the Z value is still reasonable based on trainer geometry
                        logger.info(
                            "Checking if height is within trainer limits",
                        );
                        const maxZ =
                            maxTrainerLiftHeight * 25.4 +
                            launchPointToCameraDistanceMeters * 1000.0;
                        heightDiff = Math.abs(maxZ - position.z);
                        heightGood = position.z <= maxZ;
                        if (!heightGood) {
                            logger.info(
                                `Localized height too tall! Max Z: ${maxZ} (based on trainer max lift height), Localized Z: ${position.z}, Height Diff: ${heightDiff}`,
                            );
                        }
                    }

                    const yawDiff = absoluteDiff(
                        plannedPosition.yaw,
                        position.yaw,
                    );
                    const yawGood = yawDiff < degToRad(yawDeltaAllowed);

                    logger.info("[serveAndVolley] closeEnough summary", {
                        plannedPosition,
                        position,
                        distance,
                        distanceGood,
                        heightDiff,
                        heightGood,
                        yawGood,
                        yawDiff,
                    });

                    // return true;
                    const close = distanceGood && heightGood && yawGood;
                    if (close) {
                        logger.info(
                            `[usePosition]: returning good from checkPosition`,
                        );
                        return "good";
                    } else {
                        logger.info(
                            `[usePosition]: returning improve from checkPosition`,
                        );
                        return "improve";
                    }
                } else {
                    // logger.info(
                    //     '"[usePosition] closeEnough - using new localization status',
                    // );
                    // new localization status includes the tolerance we used... we don't need to
                    // do any client side checks for whether we're close enough or not, just use
                    if (!status?.localization) {
                        return "fail";
                    }

                    const { cameraPosition, accurate, improve, errormsg } =
                        status.localization;

                    if (cameraPosition === null) {
                        // logger.warn(
                        //     `[usePosition] closeEnough - no camera position available: ${errormsg}`,
                        // );
                        return "fail";
                    } else if (improve) {
                        logger.info(
                            `[usePosition] closeEnough - camera position is close enough, but user improvable: ${errormsg}`,
                        );
                        return "improve";
                    } else if (accurate) {
                        logger.info(
                            `[usePosition] closeEnough - camera position is accurate: ${errormsg}`,
                        );
                        return "good";
                    } else {
                        // logger.warn(
                        //     `[usePosition] closeEnough - not accurate or improve, failing: ${errormsg} - ${JSON.stringify(cameraPosition)}`,
                        // );
                        return "fail";
                    }
                }
            },
            [
                stateInternal.ualResult,
                stateInternal.position,
                maxTrainerLiftHeight,
                status?.localization,
            ],
        );

        const isPlannedPositionCloseEnough = React.useCallback(
            (
                plannedPosition: PlannedPosition,
                sport: Sport,
                physicsModelName: PhysicsModelName,
                compareHeight: boolean,
            ) => {
                const currentCloseEnough = isCloseEnough(
                    plannedPosition,
                    sport,
                    physicsModelName,
                    compareHeight,
                );
                if (currentCloseEnough !== "good") {
                    return currentCloseEnough;
                }

                if (
                    !isDeprecatedLocalizationStatus(status?.localization) &&
                    status?.localization.cameraHintPosition
                ) {
                    const distanceDeltaAllowed =
                        DISTANCE_DELTA_ALLOWED_BY_SPORT[sport];
                    const yawDeltaAllowed = YAW_DELTA_ALLOWED_BY_SPORT[sport];
                    let comparePosition = plannedPosition;
                    if (sport !== "PLATFORM_TENNIS") {
                        logger.info(
                            `Converting Planned Position: ${JSON.stringify(comparePosition)} for comparison`,
                        );
                        setPhysicsModel(physicsModelName, sport);
                        const asLocalized = physics2localization({
                            x: comparePosition.x,
                            y: comparePosition.y,
                            z: launchHeightInches2HeadHeight(
                                comparePosition.heightIn,
                            ),
                        });
                        comparePosition = {
                            x: asLocalized.x,
                            y: asLocalized.y,
                            yaw: degToRad(comparePosition.yaw),
                            heightIn: comparePosition.heightIn,
                        };
                        logger.info(
                            `Updated Compare Position: ${JSON.stringify(comparePosition)}`,
                        );
                    }

                    const distance = Math.sqrt(
                        (status.localization.cameraHintPosition.x -
                            comparePosition.x) **
                            2 +
                            (status.localization.cameraHintPosition.y -
                                comparePosition.y) **
                                2,
                    );
                    const distanceGood = distance <= distanceDeltaAllowed;

                    const yawDiff = absoluteDiff(
                        comparePosition.yaw,
                        status.localization.cameraHintPosition.yaw,
                    );
                    const yawGood = yawDiff < degToRad(yawDeltaAllowed);

                    return distanceGood && yawGood ? "good" : "improve";
                }

                return currentCloseEnough;
            },
            [isCloseEnough, status?.localization],
        );

        const setUalResult = React.useCallback(
            (result: CameraPosition | null) => {
                logger.info(
                    `[usePosition] - Caching UAL result: ${JSON.stringify(result)}`,
                );
                dispatch({ type: "set-ual-result", value: result });
            },
            [],
        );

        const clearUalResult = React.useCallback(() => {
            logger.info(`[usePosition] - Clearing locally cached UAL result`);
            dispatch({ type: "clear-ual-result" });
        }, []);

        const state: PositionState = React.useMemo(
            () => ({
                ...stateInternal,
                isCanceled: stateInternal.localizationStatus === "cancelling",
                isLocalizing: stateInternal.localizationStatus !== "idle",
                cancel,
                updatePosition: fetchPosition,
                setUalResult,
                clearUalResult,
                isCloseEnough,
                isPlannedPositionCloseEnough,
            }),
            [
                cancel,
                stateInternal,
                fetchPosition,
                setUalResult,
                clearUalResult,
                isCloseEnough,
                isPlannedPositionCloseEnough,
            ],
        );

        return (
            <LocalizationContext.Provider
                value={status === null ? defaultLocalizationState : state}
            >
                {children}
            </LocalizationContext.Provider>
        );
    },
);

LocalizationProvider.displayName = "LocalizationProvider";

export default function usePosition(): PositionState {
    return React.useContext(LocalizationContext);
}
